import { FC, useRef, useState } from "react";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import ButtonComponent from "../../../../../common/components/button/button.component";
import useKeyboardPress from "../../../../../common/hooks/use-keyboard-press";
import OrderOptimizerTaxiListingFilter from "../common/types/order-optimizer-taxi-listing-filter";
import OrderOptimizerTaxiListingFilterType from "../common/types/order-optimizer-taxi-listing-filter-type";
import ListingFilterDefinition from "../../../../../common/components/listing/filter/types/listing-filter-definition";
import orderOptimizerTaxiListingFiltersHelper from "./order-optimizer-taxi-listing-filters.helper";
import ListingFilterComponent from "../../../../../common/components/listing/filter/listing-filter.component";
import OrderOptimizerTaxiListingMaximumDelayModalComponent from "../maximum-delay-modal/order-optimizer-taxi-listing-maximum-delay-modal.component";
import useOpen from "../../../../../common/hooks/use-open";
import OrderOptimizerTaxiListingMaximumHaltingTimeModalComponent from "../maximum-halting-time-modal/order-optimizer-taxi-listing-maximum-halting-time-modal.component";

type OrderOptimizerTaxiListingFiltersProps = {
  filters: OrderOptimizerTaxiListingFilter[];
  isListingLoading: boolean;
  onFiltersChange: (filters: OrderOptimizerTaxiListingFilter[]) => void;
};

const OrderOptimizerTaxiListingFiltersComponent: FC<
  OrderOptimizerTaxiListingFiltersProps
> = (props) => {
  const translations =
    orderOptimizerTranslationsHelper.getTaxiListingTranslations().filters;

  const maximumDelayModal = useOpen();
  const maximumHaltingTimeModal = useOpen();

  const [maximumDelayValue, setMaximumDelayValue] = useState<number>(0);
  const [maximumHaltingTimeValue, setMaximumHaltingTimeValue] =
    useState<number>(0);

  const confirmMaximumDelay = () => {
    const newFilter: OrderOptimizerTaxiListingFilter = {
      type: OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY,
      value: maximumDelayValue,
    };
    props.onFiltersChange([
      ...props.filters.filter(
        (filter) =>
          filter.type !== OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY,
      ),
      newFilter,
    ]);

    maximumDelayModal.close();
  };

  const confirmMaximumHaltingTime = () => {
    const newFilter: OrderOptimizerTaxiListingFilter = {
      type: OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME,
      value: maximumHaltingTimeValue,
    };
    props.onFiltersChange([
      ...props.filters.filter(
        (filter) =>
          filter.type !==
          OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME,
      ),
      newFilter,
    ]);

    maximumHaltingTimeModal.close();
  };

  const openMaximumDelayModal = () => {
    setMaximumDelayValue(0);
    maximumDelayModal.open();
  };

  const closeMaximumDelayModal = () => {
    maximumDelayModal.close();
    setMaximumDelayValue(0);
  };

  const openMaximumHaltingTimeModal = () => {
    setMaximumHaltingTimeValue(0);
    maximumHaltingTimeModal.open();
  };

  const closeMaximumHaltingTimeModal = () => {
    maximumHaltingTimeModal.close();
    setMaximumHaltingTimeValue(0);
  };

  const [filterSearchQuery, setFilterSearchQuery] = useState<{
    orderHumanId: number | null;
    maximumDelay: number | null;
    maximumHaltingTime: number | null;
    ignoreDriverRoutes: boolean;
  }>(() => ({
    orderHumanId:
      props.filters.find(
        (filter) =>
          filter.type === OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID,
      )?.value ?? null,
    maximumDelay:
      props.filters.find(
        (filter) =>
          filter.type === OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY,
      )?.value ?? null,
    maximumHaltingTime:
      props.filters.find(
        (filter) =>
          filter.type ===
          OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME,
      )?.value ?? null,
    ignoreDriverRoutes: props.filters.some(
      (filter) =>
        filter.type ===
        OrderOptimizerTaxiListingFilterType.IGNORE_DRIVER_ROUTES,
    ),
  }));

  const inputRef = useRef<HTMLInputElement>(null);

  useKeyboardPress({
    Enter: () => addFilter(),
    ref: inputRef,
  });

  const addFilter = () => {
    const newAppliedFilters: OrderOptimizerTaxiListingFilter[] = [];

    if (filterSearchQuery.orderHumanId) {
      newAppliedFilters.push({
        type: OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID,
        value: filterSearchQuery.orderHumanId,
      });
    }

    if (filterSearchQuery.maximumDelay) {
      newAppliedFilters.push({
        type: OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY,
        value: filterSearchQuery.maximumDelay,
      });
    }

    if (filterSearchQuery.maximumHaltingTime) {
      newAppliedFilters.push({
        type: OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME,
        value: filterSearchQuery.maximumHaltingTime,
      });
    }

    props.onFiltersChange(newAppliedFilters);
  };

  const filterDefinition: ListingFilterDefinition<OrderOptimizerTaxiListingFilter> =
    orderOptimizerTaxiListingFiltersHelper().getFilterDefinition(
      openMaximumDelayModal,
      openMaximumHaltingTimeModal,
    );

  return (
    <div className="order_optimizer_taxi_listing_filter">
      <div className="order_optimizer_taxi_listing_filter__input_wrapper">
        <ListingFilterComponent
          appliedFilters={props.filters}
          filterDefinition={filterDefinition}
          onFiltersChange={(appliedFilters) => {
            props.onFiltersChange(
              appliedFilters as OrderOptimizerTaxiListingFilter[],
            );
          }}
          sortDefinition={{ options: [] }}
          appliedSortKey=""
          onSortKeyChange={() => {}}
        />
      </div>
      <ButtonComponent
        onClick={() => {
          setFilterSearchQuery({
            orderHumanId: null,
            maximumDelay: null,
            maximumHaltingTime: null,
            ignoreDriverRoutes: false,
          });
          props.onFiltersChange([]);
        }}
        isLoading={props.isListingLoading}
        classNames={{
          root: "order_optimizer_Taxi_listing_filter__button",
        }}
        title={translations.getAllOrdersButtonLabel}
        type="primary"
        idForTesting="order-optimizer-listing-filter-button"
      >
        {translations.getAllOrdersButtonLabel}
      </ButtonComponent>
      <OrderOptimizerTaxiListingMaximumDelayModalComponent
        isOpen={maximumDelayModal.isOpen}
        maximumDelayValue={maximumDelayValue}
        onCloseClick={closeMaximumDelayModal}
        onSuccessClick={confirmMaximumDelay}
        setMaximumDelayValue={setMaximumDelayValue}
      />
      <OrderOptimizerTaxiListingMaximumHaltingTimeModalComponent
        isOpen={maximumHaltingTimeModal.isOpen}
        maximumHaltingTimeValue={maximumHaltingTimeValue}
        onCloseClick={closeMaximumHaltingTimeModal}
        onSuccessClick={confirmMaximumHaltingTime}
        setMaximumHaltingTimeValue={setMaximumHaltingTimeValue}
      />
    </div>
  );
};

export default OrderOptimizerTaxiListingFiltersComponent;
